/**
 * ticketEvents.js — append-only audit log for ticket lifecycle changes.
 *
 * WHY THIS EXISTS
 * `ticketChecks` is a mutable current-state document: sqFsReopen nulls
 * completedAt / timeTakenMin / prepTimeMin / prepSource, and sqFsComplete
 * overwrites timeTakenMin in place. Once an order is reopened there is no
 * record of how long it originally took — the fact is gone, not archived.
 * That is why ticketTimingUtils needs ~1,100 lines to RECONSTRUCT elapsed time
 * from whatever stamps happened to survive.
 *
 * This module appends an immutable fact for every state change instead. Nothing
 * reads from it yet — it is deliberately additive, so the existing read paths
 * and behaviour are untouched while the log accumulates the history that later
 * work (rollups, true reopen accounting, an audit view) can derive state from.
 *
 * DESIGN RULES
 *  - Append only. Never update or delete an event.
 *  - Fire-and-forget. A logging failure must NEVER block or fail a kitchen
 *    action — the chef bumping an item matters more than the audit trail.
 *  - Cheap. One small doc per state change, no reads.
 */
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

/** Event vocabulary. Past tense — these are facts that happened. */
export const TICKET_EVENTS = {
  ITEM_CHECKED:   'item.checked',
  ITEM_UNCHECKED: 'item.unchecked',
  ITEM_CHEF_DONE: 'item.chefDone',
  ITEM_CHEF_UNDONE: 'item.chefUndone',
  ASSIGNED:       'order.assigned',
  UNASSIGNED:     'order.unassigned',
  COMPLETED:      'order.completed',
  REOPENED:       'order.reopened',
  REOPENED_NEW_ITEMS: 'order.reopenedForNewItems',
  QUALITY_TAGGED: 'order.qualityTagged',
};

/** Strip undefined — Firestore rejects undefined values outright. */
function clean(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => { if (v !== undefined) out[k] = v; });
  return out;
}

/**
 * Append one immutable event.
 *
 * Returns a promise that ALWAYS resolves (never rejects), so callers can `void`
 * it without an unhandled rejection and without wrapping every call site.
 *
 * @param {string} orderId
 * @param {string} type — one of TICKET_EVENTS
 * @param {object} [payload] — the facts specific to this event
 * @returns {Promise<string|null>} new event id, or null if the write failed
 */
export async function logTicketEvent(orderId, type, payload = {}) {
  if (!orderId || !type) return null;
  try {
    const ref = await addDoc(collection(db, 'ticketEvents'), clean({
      orderId,
      type,
      ...payload,
      // Client clock for immediate ordering, server clock as the trusted value.
      atMs: Date.now(),
      at: serverTimestamp(),
    }));
    return ref.id;
  } catch (e) {
    // Swallowed on purpose — see DESIGN RULES above.
    console.warn('[ticketEvents] append failed (non-fatal):', e.message);
    return null;
  }
}

/**
 * Snapshot the timing facts that a reopen is about to erase.
 * Called BEFORE the destructive write so the values are still readable.
 */
export function timingSnapshot(data) {
  if (!data) return {};
  return clean({
    prevTimeTakenMin: data.timeTakenMin ?? null,
    prevPrepTimeMin: data.prepTimeMin ?? null,
    prevPrepSource: data.prepSource ?? null,
    prevCheckedCount: Array.isArray(data.checkedItems) ? data.checkedItems.length : 0,
    prevCompletedAtMs: data.completedAt?.toDate ? data.completedAt.toDate().getTime() : null,
  });
}
